import { Radio, Select, Typography } from 'antd';
import React from 'react';
import Linkify from '@guuru/react-linkify';
import { i18n } from '@guuru/translation-web';
import CheckboxInput from '../../../CheckboxInput';
import NumberInput from '../../../NumberInput';
import RadioOption from '../../../RadioOptionInput';
import { FormItem } from '../../../SearchInput';
import { SelectInput } from '../../../SelectInput';
import TextInput from '../../../TextInput';
import ContactsExtra from './ContactsExtra';

const { Paragraph } = Typography;

interface Field {
  mandatory: boolean;
  field: string;
  fieldType: string;
  target: string;
  helpMessage: string;
  fieldOptions: Record<string, string>;
}

const getRules = function (field: Field) {
  const rules: React.ComponentProps<typeof FormItem>['rules'] = [];
  if (field.mandatory) {
    const message = i18n.t('chat:::requiredField');
    if (field.fieldType === 'checkbox') {
      rules.push({
        required: true,
        type: 'boolean',
        transform: (value: any) => value || undefined,
        message,
      });
    } else if (field.fieldType === 'input') {
      rules.push({
        required: true,
        type: 'string',
        whitespace: true,
        message,
      });
    } else {
      rules.push({
        required: true,
        message,
      });
    }
  }
  if (field.fieldType === 'number') {
    const message = i18n.t('chat:::numericField');
    rules.push({ type: 'number', message });
  }
  return rules;
};

const getExtra = function (helpMessage: string) {
  if (!helpMessage) {
    return null;
  }
  return <Linkify text={helpMessage} />;
};

interface CustomFieldsProps {
  customFields?: Field[];
  readOnly?: boolean;
  selectedTarget?: number | string;
}

const CustomFields = function ({
  customFields,
  readOnly = false,
  selectedTarget = 0,
}: CustomFieldsProps) {
  if (!customFields || readOnly) {
    return null;
  }
  const renderInputField = function (fieldKey: string, currentField: Field) {
    const extra = getExtra(currentField.helpMessage);
    const rules = getRules(currentField);
    switch (currentField.fieldType) {
      case 'radio':
      case 'select': {
        const optionValues = Array.isArray(currentField?.fieldOptions)
          ? currentField?.fieldOptions
          : Object.values(currentField?.fieldOptions || {});
        return (
          <>
            {currentField.fieldType === 'radio' && (
              <Paragraph style={{ marginBottom: 0 }}>
                {currentField.field}
              </Paragraph>
            )}
            <FormItem name={fieldKey} extra={extra} rules={rules}>
              {currentField.fieldType === 'radio' ? (
                <Radio.Group vertical aria-label={currentField.field}>
                  {optionValues.map((optionValue, index) => (
                    <RadioOption
                      value={optionValue.field}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`optKey-${index}`}
                    >
                      {optionValue.field}
                    </RadioOption>
                  ))}
                </Radio.Group>
              ) : (
                <SelectInput allowClear placeholder={currentField.field}>
                  {optionValues.map((optionValue, index) => (
                    <Select.Option
                      value={optionValue.field}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`optKey-${index}`}
                    >
                      {optionValue.field}
                    </Select.Option>
                  ))}
                </SelectInput>
              )}
            </FormItem>
          </>
        );
      }
      case 'checkbox': {
        return (
          <>
            <Paragraph style={{ marginBottom: 0 }}>
              {currentField.field}
            </Paragraph>
            <FormItem
              name={fieldKey}
              valuePropName="checked"
              extra={extra}
              rules={rules}
            >
              <CheckboxInput aria-label={currentField.field} />
            </FormItem>
          </>
        );
      }
      case 'number': {
        return (
          <FormItem name={fieldKey} extra={extra} rules={rules}>
            <NumberInput
              maxLength={17}
              aria-label={currentField.field}
              placeholder={currentField.field}
              style={{ width: '100%' }}
              precision={0}
            />
          </FormItem>
        );
      }
      default:
        return (
          <FormItem name={fieldKey} extra={extra} rules={rules}>
            <TextInput placeholder={currentField.field} />
          </FormItem>
        );
    }
  };

  return (
    <ContactsExtra>
      {customFields
        .filter(
          ({ target }) => !target || Number(target) === Number(selectedTarget),
        )
        .map((customOption) => {
          const currentFieldName = customOption.field;
          return (
            <FormItem key={currentFieldName}>
              {renderInputField(currentFieldName, customOption)}
            </FormItem>
          );
        })}
    </ContactsExtra>
  );
};

export default CustomFields;
